"use client"

import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"

const LOCALE_PREFIXES = ["en", "cs"] // "vi" is default — no prefix

function extractSlugFromPathname(pathname: string): {
  slug: string
  basePath: string
  detectedLocale: string
} {
  const parts = pathname.split("/").filter(Boolean)
  if (parts.length === 0) return { slug: "", basePath: "", detectedLocale: "vi" }

  if (LOCALE_PREFIXES.includes(parts[0])) {
    const rest = parts.slice(1) // drop locale prefix
    return {
      slug: rest[rest.length - 1],
      basePath: rest.slice(0, -1).join("/"),
      detectedLocale: parts[0],
    }
  }

  return {
    slug: parts[parts.length - 1],
    basePath: parts.slice(0, -1).join("/"),
    detectedLocale: "vi",
  }
}

function buildLocaleUrl(locale: string, slug: string, basePath: string): string {
  const fullPath = basePath ? `${basePath}/${slug}` : slug
  if (!fullPath) return locale === "vi" ? "/" : `/${locale}`
  return locale === "vi" ? `/${fullPath}` : `/${locale}/${fullPath}`
}

export function useLocaleSwitcherLinks(currentLocale: string): Record<string, string> {
  const pathname = usePathname()
  const [localeLinks, setLocaleLinks] = useState<Record<string, string>>({})

  useEffect(() => {
    const { slug, basePath, detectedLocale } = extractSlugFromPathname(pathname)
    if (!slug) {
      setLocaleLinks({})
      return
    }

    const locale = currentLocale || detectedLocale

    // basePath giúp API biết loại nội dung (vd "stores" → tra theo id + localizations
    // vì mỗi locale của store là entity id khác nhau, không tra theo slug được).
    fetch(
      `/api/locale-slug?slug=${encodeURIComponent(slug)}&locale=${locale}&basePath=${encodeURIComponent(basePath)}`,
    )
      .then(res => res.json())
      .then((data: Record<string, string>) => {
        if (!data || Object.keys(data).length === 0) {
          setLocaleLinks({})
          return
        }
        const links: Record<string, string> = {}
        for (const [loc, locSlug] of Object.entries(data)) {
          links[loc] = buildLocaleUrl(loc, locSlug, basePath)
        }
        setLocaleLinks(links)
      })
      .catch(() => {
        setLocaleLinks({})
        // fallback to static hrefs handled in consumer
      })
  }, [pathname, currentLocale])

  return localeLinks
}
